import { CustomDataset, CustomDatasetList } from "data/models";
import { fetchCustomDatasetsLink } from "data/queries-common";
import { formatBytes } from "utils/commons";

import { ColumnDef } from "@tanstack/react-table";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { BookText, Download, EllipsisVertical } from "lucide-react";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Badge } from "components/ui/badge";

type Dictionary = {
  [key: string]: string;
};

const DisplayFormatMapping: Dictionary = {
  GDB: "GDB",
  GEOJSON: "GeoJSON",
  SHP: "Shapefile",
  TAB: "MapInfo TAB",
  LYNX: "LYNX",
  PUMP: "PUMP",
  GEOTIFF: "GEOTIFF",
  PSV: "Pipe-Separated Values",
};

const DisplayStateMapping: Dictionary = {
  "ALL STATES": "All States",
  AUSTRALIA: "Australia",
};

function formatDate(date: string) {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const [year, month, day] = date.split("-");
  const monthIndex = parseInt(month) - 1;

  const formattedDate = `${parseInt(day)} ${months[monthIndex]}, ${parseInt(
    year,
  )}`;
  return formattedDate;
}

type CustomDatasetRow = {
  id: string;
  name: string;
  region: string;
  format: string;
  size: string | number;
  release: string;
  version: string;
  publishDate: string;
  expiry?: number | null;
  licenced: boolean;
  datum: string;
  status: string;
  docsLink?: string;
};

function ActionCell({ row }: { row: CustomDatasetRow }) {
  const { enqueueNotImplementedSnackbar } = useCustomSnackbars();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={async () => {
            try {
              const response = await fetchCustomDatasetsLink(row.id);

              const a = document.createElement("a");
              a.href = response;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(response);
            } catch (er) {
              enqueueNotImplementedSnackbar();
            }
          }}
        >
          <Download className="size-4" />
          Download
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled={!row.docsLink}
          onClick={() => {
            if (!row.docsLink) return;
            const url = row.docsLink.startsWith("http")
              ? row.docsLink
              : `https://${row.docsLink}`;
            window.open(url, "_blank");
          }}
        >
          <BookText className="size-4" />
          Docs
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const customColumns = (): ColumnDef<CustomDatasetRow>[] => {
  return [
    {
      accessorKey: "name",
      header: "Name",
      enableSorting: true,
    },
    {
      accessorKey: "region",
      header: "Regions",
      size: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return DisplayStateMapping[val] ?? val;
      },
    },
    {
      accessorKey: "format",
      header: "Format",
      size: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return DisplayFormatMapping[val] ?? val;
      },
    },
    {
      accessorKey: "datum",
      header: "Datum",
      size: 80,
      enableSorting: true,
    },
    {
      accessorKey: "release",
      header: "Release Date",
      enableSorting: true,
      size: 80,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        const [year, month] = val.split("-").map(Number);
        const monthName = new Date(year, month - 1).toLocaleString("default", {
          month: "long",
        });
        return `${monthName} ${year}`;
      },
    },
    {
      accessorKey: "publishDate",
      header: "Publish Date",
      size: 80,
      enableSorting: true,
      cell: ({ getValue }) => formatDate(getValue() as string),
    },
    {
      accessorKey: "expiry",
      header: "Expiry",
      size: 80,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as number | null;
        if (val) {
          const date = new Date(val);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return formatDate(`${year}-${month}-${day}`);
        }
        return "";
      },
    },
    {
      accessorKey: "version",
      header: "Version",
      enableSorting: true,
      size: 70,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? <Badge variant="secondary">{val}</Badge> : null;
      },
    },
    {
      accessorKey: "size",
      header: "File Size",
      size: 70,
      enableSorting: true,
      cell: ({ getValue }) => formatBytes(getValue() as number),
    },
    {
      id: "actions",
      header: "",
      size: 48,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <ActionCell row={row.original} />
        </div>
      ),
    },
  ];
};

// Opportunity-based deliveries append `_<clip_id[:8]>` to the dataset name so
// datasets_api's uniqueness key (apigeeDeveloperId+name+release+region+format+datum)
// doesn't collide when users reuse the same dataset name across opportunities.
// The suffix is display-only noise — strip it back off here.
const OPPORTUNITY_CREATOR = "opportunity-delivery";
const OPPORTUNITY_SUFFIX_PATTERN = /_[0-9a-f]{8}$/;

function displayName(dataset: CustomDataset): string {
  if (dataset.creator === OPPORTUNITY_CREATOR) {
    return dataset.name.replace(OPPORTUNITY_SUFFIX_PATTERN, "");
  }
  return dataset.name;
}

const customersToRows = (
  datasetsList: CustomDatasetList,
): CustomDatasetRow[] => {
  const rows = datasetsList.datasets.map((key: CustomDataset) => {
    return {
      id: key.id,
      name: displayName(key),
      region: key.region,
      format: key.format,
      size: key.size,
      release: key.release,
      version: key.version,
      publishDate: key.publishDate,
      expiry: key.expiry,
      licenced: key.licenced,
      datum: key.datum,
      status: key.status,
      docsLink: key.docsLink,
    };
  });
  return rows;
};

export { customColumns, customersToRows };
export type { CustomDatasetRow };
